'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import NumberToPrice from '@/_components/NumberToPrice';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { branchService } from '@/_service/branch.service';
import orderService from '@/_service/orders.service';
import reviewService from '@/_service/review.service';
import { getSettings } from '@/_service/settings.service';
import { getStatusName, getTrackingStatus } from '@/_utils/getStatus';
import { Alert } from '@/components/ui/alert';
import { toaster } from '@/components/ui/toaster';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';
import PreviewDesktop from './responsive/desktop';
import PreviewMobile from './responsive/mobile';

type Props = {
  id: string;
  shipperId: string;
};

const Preview: React.FC<Props> = ({ id, shipperId }) => {
  const [sent, setSent] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);

  const t = useTranslations();
  const currentLocale = useCustomLocale();
  const isDesktop = useIsDesktop();

  const {
    data: order,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['order-by-id', id, shipperId],
    queryFn: () => orderService.getById(id),
    enabled: Boolean(id),
    refetchInterval,
  });
  console.log(order);

  const { data: settingsData } = useQuery({
    queryKey: ['branch-by-id', id, order?.data?.steps?.[0]?.branch_id],
    queryFn: () =>
      getSettings({
        page: 1,
        limit: 1,
        object_id: order?.data?.steps?.[0]?.branch_id || '',
        object_type: 'branch',
        fields: 'product_cooking_time',
      }).then((res) => res?.data),
    enabled: Boolean(order?.data?.steps?.[0]?.branch_id),
  });

  const { data: branch } = useQuery({
    queryKey: ['branch-by-id', id],
    queryFn: () =>
      branchService
        .getBranchById(order?.data?.steps?.[0]?.branch_id || '')
        .then((res) => res?.data),
    enabled: Boolean(order?.data?.steps?.[0]?.branch_id),
  });

  useEffect(() => {
    if (order?.data) {
      const activeStatuses = [
        'new',
        'accepted',
        'getting_ready',
        'ready',
        'courier_picked',
      ];
      if (activeStatuses.includes(getTrackingStatus(order?.data?.status_id))) {
        setRefetchInterval(10000); // Reduced from 10s to 15s, and only for active orders
      } else {
        setRefetchInterval(false); // Stop polling for completed/cancelled orders
      }
    }
  }, [order?.data]);

  dayjs.locale(currentLocale);
  const futureTime = order?.data?.future_time
    ? dayjs(order?.data?.future_time).format('D MMMM HH:mm')
    : '';

  const status = order?.data?.status_id
    ? getTrackingStatus(order.data?.status_id)
    : '';
  const isKaspiCase =
    status !== 'future' && status !== 'new' && status !== 'accepted';

  const isCashback = order?.data?.payment?.find(
    (el) => el.payment_type === 'cashback',
  );
  const total = useMemo(() => {
    if (order) {
      return isCashback
        ? order?.data?.delivery_price +
            order?.data?.order_amount -
            isCashback?.paid_amount
        : (order?.data?.is_delivery_free ? 0 : order?.data?.delivery_price) +
            order?.data?.order_amount || 0;
    } else {
      return 0;
    }
  }, [order]);

  const change =
    Number(order?.data?.given_cash || 0) > total
      ? Number(order?.data?.given_cash || 0) - total
      : 0;

  const onSubmitReview = (payload: { rate: number; reviewMsg: string }) => {
    const data = {
      lang: currentLocale,
      branch_id: order?.data?.steps?.[0]?.branch_id,
      branch_name: order?.data?.steps?.[0]?.branch_name,
      client_id: order?.data?.client_id,
      client_mail: '',
      client_name: order?.data?.client_name,
      client_phone: order?.data?.client_phone_number,
      courier_id: order?.data?.courier_id,
      courier_name: `${order?.data?.courier?.first_name} ${order?.data?.courier?.last_name}`,
      delivery_time: order?.data?.delivery_time,
      operator_id: order?.data?.shipper_user?.id,
      operator_name: order?.data?.shipper_user?.name,
      order_id: order?.data?.id,
      order_num: order?.data?.external_order_id,
      rating: payload.rate,
      related_subject: 'meal', // actually, 'order' (misnamed)
      review_message: payload.reviewMsg,
      type: payload.rate > 3 ? 'like' : 'dislike',
    };
    reviewService
      .create(data)
      .then(() => {
        toaster.success({
          title: t('successfully_sent'),
        });
        setSent(true);
        refetch();
      })
      .catch(console.log);
  };

  const renderStatusText = () => {
    const interval =
      (order?.data?.delivery_type === 'delivery'
        ? branch?.delivery_time
        : Number(settingsData?.settings?.[0]?.value?.value || 0) ||
          branch?.delivery_time) || 0;

    const fromTime = dayjs(order?.data?.created_at)
      .add(interval, 'minute')
      .format('HH:mm');

    const toTime = dayjs(order?.data?.created_at)
      .add(interval, 'minute')
      .add(15, 'minute')
      .format('HH:mm');

    if (isKaspiCase && order?.data?.payment_type_obj?.integration === 'kaspi') {
      return (
        <Text color='gray.600' mt={3}>
          {order?.data?.status_id &&
            t(`order_list.${getStatusName(order.data?.status_id)}`)}
        </Text>
      );
    }
    if (
      status !== 'finished' &&
      status !== 'delivered' &&
      order?.data?.order_type?.type === 'hall' &&
      order?.data?.pos_id
    ) {
      return (
        <Box textAlign={'center'}>
          <Text fontSize='2xl' fontWeight={700}>
            {order?.data?.status_id && t(`order_list.vendor-accepted`)}
          </Text>
          <Text
            mt={2}
            as={'span'}
            fontSize='sm'
            lineHeight='17px'
            color='gray.600'
            fontWeight={400}
          >
            {order?.data?.future_time
              ? futureTime
              : t('takeaway_time_text', {
                  from: fromTime,
                  to: toTime,
                })}
          </Text>
        </Box>
      );
    }
    if (
      status !== 'finished' &&
      status !== 'delivered' &&
      order?.data?.payment_type === 'online' &&
      !order?.data?.paid
    ) {
      return (
        <Box textAlign={'center'}>
          <Text fontSize='2xl' fontWeight={700}>
            {t('payment_not_completed')}
          </Text>
          <Text
            mt={2}
            fontSize='sm'
            lineHeight='17px'
            color='gray.600'
            fontWeight={400}
            as={'span'}
          >
            {t('please_complete_the_payment')}{' '}
            <NumberToPrice
              parentProps={{
                display: 'inline-flex',
              }}
              textProps={{
                as: 'span',
              }}
              value={
                order?.data?.order_amount + (order?.data?.service_fee || 0)
              }
            />
            , {t('so_that_we_can_process_your_order')}
          </Text>
        </Box>
      );
    }
    if (status !== 'finished' && status !== 'delivered') {
      return (
        <Box textAlign={'center'}>
          <Text
            fontSize='sm'
            lineHeight='17px'
            color='gray.600'
            fontWeight={400}
          >
            {order?.data?.status_id &&
              t(`order_list.${getStatusName(order.data?.status_id)}`)}
          </Text>
        </Box>
      );
    }

    return (
      <Text fontSize={{ base: 'xs', lg: 'md' }} color='gray.600' mt={3}>
        {order?.data?.status_id &&
          t(`order_list.${getStatusName(order.data?.status_id)}`)}
      </Text>
    );
  };

  if (isLoading)
    return (
      <Center alignItems={'center'} justifyContent={'center'} h={'80vh'}>
        <Spinner color='primary' size='lg' />
      </Center>
    );

  if (isError) return <Alert status='error' title='Internal Server Error!' />;
  if (!order) return null;

  const commonProps = {
    id,
    sent,
    order: order?.data,
    total,
    change,
    status,
    refetch,
    onSubmitReview,
    renderStatusText,
  };

  return isDesktop ? (
    <PreviewDesktop {...commonProps} />
  ) : (
    <PreviewMobile {...commonProps} />
  );
};

export default Preview;
